import { IconPlus, IconSearch } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import z from "zod";
import { CreateChannelDialog } from "@/components/forms/channels/create";
import { Button } from "@/components/ui/button";
import {
	DataTableGeneric,
	type DataTableSearchParams,
	DataTableSearchSchema,
} from "@/components/ui/data-table-generic";
import { Input } from "@/components/ui/input";
import { orpc } from "@/orpc/client";
import { columns } from "@/tables/publisher-channels";

export const Route = createFileRoute("/_app/publishers/$publisherId/channels")({
	component: Page,
	validateSearch: (search) => DataTableSearchSchema.parse(search),
	params: z.object({
		publisherId: z.coerce.number(),
	}),
	async loader({ context, params }) {
		context.queryClient.prefetchQuery(
			orpc.publishers.show.queryOptions({
				input: {
					publisherId: params.publisherId,
				},
			}),
		);
		context.queryClient.prefetchQuery(
			orpc.publishers.listChannels.queryOptions({
				input: {
					publisherId: params.publisherId,
				},
			}),
		);
	},
});

function Page() {
	const params = Route.useParams();
	const search = Route.useSearch();
	const navigate = Route.useNavigate();

	const { data, isFetching, refetch } = useSuspenseQuery(
		orpc.publishers.listChannels.queryOptions({
			input: {
				publisherId: params.publisherId,
			},
		}),
	);

	const [globalFilter, setGlobalFilter] = useState(search.q ?? "");

	const handleNavigate = useCallback(
		(params: DataTableSearchParams) => {
			navigate({
				replace: true,
				search: (prev) => ({
					...prev,
					...params,
				}),
			});
		},
		[navigate],
	);

	const toolbar = (
		<>
			<div className="relative">
				<Input
					value={globalFilter}
					onChange={(e) => setGlobalFilter(e.target.value)}
					placeholder="Search Channels..."
					className="pl-10"
				/>
				<div className="pointer-events-none absolute left-2 top-0 h-full flex items-center justify-center">
					<IconSearch className="h-4" />
				</div>
			</div>
			<CreateChannelDialog publisherId={params.publisherId}>
				<Button>
					<IconPlus />
					Create a Channel
				</Button>
			</CreateChannelDialog>
		</>
	);

	return (
		<div className="flex flex-1 flex-col">
			<div className="@container/main flex flex-1 flex-col gap-2">
				<div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
					<DataTableGeneric
						columns={columns}
						data={data ?? []}
						isFetching={isFetching}
						handleReload={() => refetch()}
						toolbar={toolbar}
						searchParams={search}
						onNavigate={handleNavigate}
						getRowId={(row) => row.ID.toString()}
						config={{
							defaultPageSize: 25,
							pageSizeOptions: [25, 50, 100, 500],
						}}
					/>
				</div>
			</div>
		</div>
	);
}
